/**
 * personalization.js
 * ------------------------------------------------------------
 * Widget for tumblers/water bottles only. Live preview of the
 * engraving (text + logo) on the product photo: the customer can
 * DRAG and RESIZE using the handle in the bottom-right corner,
 * and ROTATE using the handle above the layer. Dragging near the
 * center of the engraving area snaps into place with a guide
 * line, both horizontally and vertically. Once a logo is
 * uploaded, a "Remove image" button appears to clear it and pick
 * a different one.
 * The logo is always converted to monochrome (real pixel-level
 * duotone, not a CSS filter), since that's exactly how the real
 * laser engraving comes out.
 *
 * Validation before submit so a personalized product can't be
 * added to the cart without spelling confirmation.
 *
 * Personalization (text and/or logo) is OPTIONAL — the customer can add
 * the tumbler/bottle to the cart with no personalization at all.
 *
 * ENGRAVING FEE: when the customer DOES add text or a logo, a €10
 * "Laser Engraving Fee" product is added to the cart automatically
 * alongside the tumbler/bottle, via two chained /cart/add.js calls
 * (Shopify doesn't support variant-level price add-ons natively on
 * the Basic plan, so this is the standard workaround). If no
 * personalization is used, the form submits normally with no fee.
 */
var ENGRAVING_FEE_VARIANT_ID = 60635339489614;
document.addEventListener('DOMContentLoaded', function () {
  document.querySelectorAll('[data-personalization-widget]').forEach(function (widget) {
    new PersonalizationWidget(widget);
  });
});

function PersonalizationWidget(root) {
  this.root = root;
  this.wrap = root.querySelector('[data-personalization-canvas-wrap]');
  this.form = root.closest('form');

  this.area = {
    x: parseFloat(this.wrap.getAttribute('data-area-x')) || 0,
    y: parseFloat(this.wrap.getAttribute('data-area-y')) || 0,
    w: parseFloat(this.wrap.getAttribute('data-area-w')) || 100,
    h: parseFloat(this.wrap.getAttribute('data-area-h')) || 100
  };

  this.textArea = root.querySelector('[data-personalization-input="text"]');
  this.logoInput = root.querySelector('[data-personalization-input="logo"]');
  this.removeLogoBtn = root.querySelector('[data-personalization-remove="logo"]');
  this.fontSelect = root.querySelector('[data-personalization-font]');
  this.fontSizeRange = root.querySelector('[data-personalization-font-size]');
  this.fontSizeOut = root.querySelector('[data-personalization-font-size-out]');
  this.confirmCheckbox = root.querySelector('[data-personalization-confirm]');
  this.errorEl = root.querySelector('[data-personalization-error]');
  this.overflowWarningEl = root.querySelector('[data-personalization-overflow-warning]');
  this.areaEl = root.querySelector('[data-personalization-area]');
  this.guideV = root.querySelector('[data-personalization-guide="v"]');
  this.guideH = root.querySelector('[data-personalization-guide="h"]');
  this.hasOverflow = false;

  this.layers = {
    text: this.setupLayer('text'),
    logo: this.setupLayer('logo')
  };

  this.bindInputs();
  this.bindFontControls();
  this.bindForm();
}

PersonalizationWidget.prototype.setupLayer = function (kind) {
  var el = this.root.querySelector('[data-personalization-layer="' + kind + '"]');
  var handle = el.querySelector('[data-personalization-handle="' + kind + '"]');
  var rotateHandle = el.querySelector('[data-personalization-rotate-handle="' + kind + '"]');
  var content = el.querySelector('[data-personalization-layer-content]');
  var posInput = this.root.querySelector('[data-personalization-pos="' + kind + '"]');
  var sizeInput = this.root.querySelector('[data-personalization-size="' + kind + '"]');
  var rotationInput = this.root.querySelector('[data-personalization-rotation="' + kind + '"]');

  var layer = {
    kind: kind,
    el: el,
    handle: handle,
    rotateHandle: rotateHandle,
    content: content,
    posInput: posInput,
    sizeInput: sizeInput,
    rotationInput: rotationInput,
    rotation: 0,
    initLeft: parseFloat(el.style.left) || 50,
    initTop: parseFloat(el.style.top) || 50,
    initFontSize: kind === 'text' ? (parseFloat(el.style.fontSize) || 28) : null,
    initWidth: kind === 'logo' ? (parseFloat(el.style.width) || 22) : null
  };

  this.applyLayerTransform(layer);
  this.makeDraggable(layer);
  this.makeResizable(layer);
  this.makeRotatable(layer);

  return layer;
};

PersonalizationWidget.prototype.applyLayerTransform = function (layer) {
  layer.el.style.transform = 'translate(-50%, -50%) rotate(' + layer.rotation + 'deg)';
};

PersonalizationWidget.prototype.updateRotationInput = function (layer, angle) {
  if (layer.rotationInput) {
    layer.rotationInput.value = String(Math.round(angle));
  }
};

PersonalizationWidget.prototype.bindInputs = function () {
  var self = this;

  if (this.textArea) {
    this.textArea.addEventListener('input', function () {
      self.renderTextLines();
    });
  }

  if (this.logoInput) {
    this.logoInput.addEventListener('change', function (e) {
      var file = e.target.files && e.target.files[0];
      if (!file) {
        self.layers.logo.el.style.display = 'none';
        if (self.removeLogoBtn) self.removeLogoBtn.hidden = true;
        return;
      }
      var tint = '#E3E3DD'; // laser engraving on metal -> light gray/silver, regardless of the original color; MUST match --pw-engrave-silver in personalization.css
      var reader = new FileReader();
      reader.onload = function (ev) {
        duotoneImage(ev.target.result, tint, function (processedDataUrl) {
          self.layers.logo.content.src = processedDataUrl;
          self.layers.logo.el.style.display = 'block';
          if (self.removeLogoBtn) self.removeLogoBtn.hidden = false;
          self.updateOverflowState();
        });
      };
      reader.readAsDataURL(file);
    });
  }

  if (this.removeLogoBtn) {
    this.removeLogoBtn.addEventListener('click', function () {
      self.removeLogo();
    });
  }
};

PersonalizationWidget.prototype.removeLogo = function () {
  var logo = this.layers.logo;

  // Clear the actual file input so submitting the form doesn't still
  // carry the old file, and so a fresh 'change' event fires even if
  // the customer picks the exact same filename again afterwards.
  if (this.logoInput) this.logoInput.value = '';

  logo.el.style.display = 'none';
  logo.content.removeAttribute('src');

  // Reset position/size/rotation back to their starting point, so a
  // newly picked logo starts fresh at the center rather than wherever
  // the removed one happened to be left.
  logo.rotation = 0;
  this.applyLayerTransform(logo);
  logo.el.style.left = logo.initLeft + '%';
  logo.el.style.top = logo.initTop + '%';
  logo.el.style.width = logo.initWidth + '%';
  this.updatePosInput(logo, logo.initLeft, logo.initTop);
  this.updateSizeInput(logo, logo.initWidth);
  this.updateRotationInput(logo, 0);

  if (this.removeLogoBtn) this.removeLogoBtn.hidden = true;
  this.updateOverflowState();
};

PersonalizationWidget.prototype.renderTextLines = function () {
  var container = this.layers.text.content;
  container.innerHTML = '';

  var lines = this.textArea.value
    .split('\n')
    .map(function (line) { return line.trim(); })
    .filter(function (value) { return value.length > 0; });

  lines.forEach(function (line) {
    var lineEl = document.createElement('div');
    lineEl.className = 'personalization-widget__text-line';
    lineEl.textContent = line;
    container.appendChild(lineEl);
  });

  this.layers.text.el.style.display = lines.length ? 'inline-block' : 'none';

  this.clampFontSizeToLineCount();
  this.updateOverflowState();
};

PersonalizationWidget.prototype.getActiveLineCount = function () {
  var count = this.textArea.value
    .split('\n')
    .filter(function (line) { return line.trim().length > 0; }).length;
  return Math.max(count, 1);
};

PersonalizationWidget.prototype.clampFontSizeToLineCount = function () {
  if (!this.wrap) return;
  var rect = this.wrap.getBoundingClientRect();
  if (!rect.height) return;
  var bounds = this.getAreaPixelBounds(rect);
  var lineCount = this.getActiveLineCount();
  var maxFontSize = Math.max(14, (bounds.h / lineCount) * 0.85);
  var currentSize = parseFloat(this.layers.text.el.style.fontSize) || 28;
  if (currentSize > maxFontSize) {
    var newSize = Math.max(14, maxFontSize);
    this.layers.text.el.style.fontSize = newSize + 'px';
    this.updateSizeInput(this.layers.text, Math.round(newSize));
    this.syncFontSizeUI(newSize);
  }
};

PersonalizationWidget.prototype.makeDraggable = function (layer) {
  var self = this;
  var dragging = false;
  var startX, startY, startLeft, startTop;
  var SNAP_PX = 7;

  layer.el.addEventListener('pointerdown', function (e) {
    if (e.target === layer.handle || e.target === layer.rotateHandle) return;
    dragging = true;
    layer.el.setPointerCapture(e.pointerId);
    layer.el.classList.add('is-dragging');
    startX = e.clientX;
    startY = e.clientY;
    var rect = self.wrap.getBoundingClientRect();
    startLeft = (parseFloat(layer.el.style.left) || 50) / 100 * rect.width;
    startTop = (parseFloat(layer.el.style.top) || 50) / 100 * rect.height;
    e.preventDefault();
  });

  layer.el.addEventListener('pointermove', function (e) {
    if (!dragging) return;
    var rect = self.wrap.getBoundingClientRect();
    var newLeftPx = startLeft + (e.clientX - startX);
    var newTopPx = startTop + (e.clientY - startY);

    var bounds = self.getAreaPixelBounds(rect);

    // Clamp the layer's CENTER point to the engraving area's edges
    // (not inset by the layer's own half-width/height). Insetting by
    // half the layer's size breaks down whenever the layer is bigger
    // than the area itself — common for a longer line of engraving
    // text — collapsing min/max to the exact same point and making
    // the layer feel permanently stuck at the center. Overflow past
    // the frame is still caught and flagged by updateOverflowState()
    // below, so freeing up the center point here doesn't let anything
    // slip through unnoticed.
    var minX = bounds.x;
    var maxX = bounds.x + bounds.w;
    var minY = bounds.y;
    var maxY = bounds.y + bounds.h;

    var clampedX = clamp(newLeftPx, minX, maxX);
    var clampedY = clamp(newTopPx, minY, maxY);

    // Snap to the engraving area's own center, horizontally and
    // vertically independently, and light up the matching guide line.
    var areaCenterX = bounds.x + bounds.w / 2;
    var areaCenterY = bounds.y + bounds.h / 2;
    var snappedV = false;
    var snappedH = false;

    if (Math.abs(clampedX - areaCenterX) < SNAP_PX) {
      clampedX = areaCenterX;
      snappedV = true;
    }
    if (Math.abs(clampedY - areaCenterY) < SNAP_PX) {
      clampedY = areaCenterY;
      snappedH = true;
    }

    if (self.guideV) self.guideV.classList.toggle('is-active', snappedV);
    if (self.guideH) self.guideH.classList.toggle('is-active', snappedH);

    var leftPct = (clampedX / rect.width) * 100;
    var topPct = (clampedY / rect.height) * 100;
    layer.el.style.left = leftPct + '%';
    layer.el.style.top = topPct + '%';
    self.updatePosInput(layer, leftPct, topPct);
    self.updateOverflowState();
  });

  function endDrag(e) {
    if (!dragging) return;
    dragging = false;
    layer.el.classList.remove('is-dragging');
    if (self.guideV) self.guideV.classList.remove('is-active');
    if (self.guideH) self.guideH.classList.remove('is-active');
  }
  layer.el.addEventListener('pointerup', endDrag);
  layer.el.addEventListener('pointercancel', endDrag);
};

PersonalizationWidget.prototype.getAreaPixelBounds = function (wrapRect) {
  return {
    x: (this.area.x / 100) * wrapRect.width,
    y: (this.area.y / 100) * wrapRect.height,
    w: (this.area.w / 100) * wrapRect.width,
    h: (this.area.h / 100) * wrapRect.height
  };
};

PersonalizationWidget.prototype.isLayerOverflowing = function (layer, bounds, wrapRect) {
  if (!layer || layer.el.style.display === 'none') return false;

  // getBoundingClientRect reports the actual, rotation-aware axis-
  // aligned bounding box, so overflow detection stays correct even
  // when the layer has been rotated.
  var elRect = layer.el.getBoundingClientRect();
  var relLeft = elRect.left - wrapRect.left;
  var relTop = elRect.top - wrapRect.top;
  var relRight = relLeft + elRect.width;
  var relBottom = relTop + elRect.height;
  var tolerance = 1;

  return (
    relLeft < bounds.x - tolerance ||
    relTop < bounds.y - tolerance ||
    relRight > bounds.x + bounds.w + tolerance ||
    relBottom > bounds.y + bounds.h + tolerance
  );
};

PersonalizationWidget.prototype.updateOverflowState = function () {
  var wrapRect = this.wrap.getBoundingClientRect();
  if (!wrapRect.width || !wrapRect.height) return;
  var bounds = this.getAreaPixelBounds(wrapRect);

  var textOverflow = this.isLayerOverflowing(this.layers.text, bounds, wrapRect);
  var logoOverflow = this.isLayerOverflowing(this.layers.logo, bounds, wrapRect);
  this.hasOverflow = textOverflow || logoOverflow;

  this.layers.text.el.classList.toggle('is-overflowing', textOverflow);
  this.layers.logo.el.classList.toggle('is-overflowing', logoOverflow);
  if (this.areaEl) this.areaEl.classList.toggle('is-overflowing', this.hasOverflow);
  if (this.overflowWarningEl) this.overflowWarningEl.hidden = !this.hasOverflow;
};

PersonalizationWidget.prototype.bindFontControls = function () {
  var self = this;

  if (this.fontSelect) {
    this.fontSelect.addEventListener('change', function () {
      self.layers.text.el.style.fontFamily = self.fontSelect.value;
      self.updateOverflowState();
    });
    this.layers.text.el.style.fontFamily = this.fontSelect.value;
  }

  if (this.fontSizeRange) {
    this.fontSizeRange.addEventListener('input', function () {
      var size = parseFloat(self.fontSizeRange.value);
      self.layers.text.el.style.fontSize = size + 'px';
      self.updateSizeInput(self.layers.text, Math.round(size));
      if (self.fontSizeOut) self.fontSizeOut.textContent = Math.round(size);
      self.updateOverflowState();
    });
  }
};

PersonalizationWidget.prototype.syncFontSizeUI = function (size) {
  if (this.fontSizeRange) this.fontSizeRange.value = Math.round(size);
  if (this.fontSizeOut) this.fontSizeOut.textContent = Math.round(size);
};

PersonalizationWidget.prototype.makeResizable = function (layer) {
  var self = this;
  var resizing = false;
  var startX, startY, startSize;

  layer.handle.addEventListener('pointerdown', function (e) {
    resizing = true;
    layer.handle.setPointerCapture(e.pointerId);
    startX = e.clientX;
    startY = e.clientY;
    startSize = layer.kind === 'text'
      ? parseFloat(layer.el.style.fontSize) || 28
      : parseFloat(layer.el.style.width) || 22;
    e.preventDefault();
    e.stopPropagation();
  });

  layer.handle.addEventListener('pointermove', function (e) {
    if (!resizing) return;
    // Project the raw screen-space drag delta back into the layer's
    // own (un-rotated) local axes, so dragging the handle outward
    // still grows the layer correctly no matter its current rotation.
    var rawDx = e.clientX - startX;
    var rawDy = e.clientY - startY;
    var local = rotateDelta(rawDx, rawDy, layer.rotation);
    var delta = local.x + local.y;

    var rect = self.wrap.getBoundingClientRect();
    var bounds = self.getAreaPixelBounds(rect);

    if (layer.kind === 'text') {
      var lineCount = self.getActiveLineCount();
      var maxFontSize = Math.max(14, (bounds.h / lineCount) * 0.85);
      var newSize = clamp(startSize + delta * 0.3, 14, Math.min(90, maxFontSize));
      layer.el.style.fontSize = newSize + 'px';
      self.updateSizeInput(layer, Math.round(newSize));
      self.syncFontSizeUI(newSize);
    } else {
      var maxWidthPctByArea = (Math.min(bounds.w, bounds.h) / rect.width) * 100 * 0.95;
      var newWidthPct = clamp(startSize + (delta / rect.width) * 100, 6, Math.min(80, maxWidthPctByArea));
      layer.el.style.width = newWidthPct + '%';
      self.updateSizeInput(layer, Math.round(newWidthPct));
    }
    self.updateOverflowState();
  });

  function endResize() {
    resizing = false;
  }
  layer.handle.addEventListener('pointerup', endResize);
  layer.handle.addEventListener('pointercancel', endResize);
};

PersonalizationWidget.prototype.makeRotatable = function (layer) {
  var self = this;
  if (!layer.rotateHandle) return;

  var rotating = false;
  var startRotation = 0;
  var pointerStartAngle = 0;
  var SNAP_DEG = 4;
  var snapAngles = [-180, -135, -90, -45, 0, 45, 90, 135, 180];

  layer.rotateHandle.addEventListener('pointerdown', function (e) {
    rotating = true;
    layer.rotateHandle.setPointerCapture(e.pointerId);
    layer.rotateHandle.classList.add('is-rotating');
    var rect = layer.el.getBoundingClientRect();
    var centerX = rect.left + rect.width / 2;
    var centerY = rect.top + rect.height / 2;
    pointerStartAngle = Math.atan2(e.clientY - centerY, e.clientX - centerX) * 180 / Math.PI;
    startRotation = layer.rotation;
    e.preventDefault();
    e.stopPropagation();
  });

  layer.rotateHandle.addEventListener('pointermove', function (e) {
    if (!rotating) return;
    var rect = layer.el.getBoundingClientRect();
    var centerX = rect.left + rect.width / 2;
    var centerY = rect.top + rect.height / 2;
    var pointerAngle = Math.atan2(e.clientY - centerY, e.clientX - centerX) * 180 / Math.PI;
    var delta = pointerAngle - pointerStartAngle;

    // Keep rotation normalized to -180..180 so it never runs away
    // across multiple turns.
    var newRotation = startRotation + delta;
    newRotation = ((newRotation + 180) % 360 + 360) % 360 - 180;

    // Snap to common angles (0/45/90/.../180) within a small
    // tolerance — the same idea as the position snap, for clean,
    // deliberate-looking results (e.g. perfectly horizontal or
    // perfectly vertical text).
    for (var i = 0; i < snapAngles.length; i++) {
      if (Math.abs(newRotation - snapAngles[i]) < SNAP_DEG) {
        newRotation = snapAngles[i];
        break;
      }
    }

    layer.rotation = newRotation;
    self.applyLayerTransform(layer);
    self.updateRotationInput(layer, newRotation);
    self.updateOverflowState();
  });

  function endRotate() {
    rotating = false;
    layer.rotateHandle.classList.remove('is-rotating');
  }
  layer.rotateHandle.addEventListener('pointerup', endRotate);
  layer.rotateHandle.addEventListener('pointercancel', endRotate);
};

PersonalizationWidget.prototype.updatePosInput = function (layer, leftPct, topPct) {
  if (layer.posInput) {
    layer.posInput.value = Math.round(leftPct) + ',' + Math.round(topPct);
  }
};

PersonalizationWidget.prototype.updateSizeInput = function (layer, size) {
  if (layer.sizeInput) {
    layer.sizeInput.value = String(size);
  }
};

PersonalizationWidget.prototype.bindForm = function () {
  var self = this;
  if (!this.form) return;

  this.form.addEventListener('submit', function (e) {
    var errorMessage = self.validate();
    if (errorMessage) {
      e.preventDefault();
      e.stopImmediatePropagation();
      self.showError(errorMessage);
      return;
    }

    var hasText = self.textArea && self.textArea.value.trim().length > 0;
    var hasLogo = self.logoInput && self.logoInput.files && self.logoInput.files.length > 0;

    if (hasText || hasLogo) {
      e.preventDefault();
      e.stopImmediatePropagation();
      self.submitWithEngravingFee();
    }
  }, true);
};

PersonalizationWidget.prototype.submitWithEngravingFee = function () {
  var self = this;
  var submitBtn = this.form.querySelector('button[type="submit"], button[name="add"]');
  if (submitBtn) {
    submitBtn.disabled = true;
    submitBtn.classList.add('is-loading');
  }

  var formData = new FormData(this.form);

  fetch('/cart/add.js', {
    method: 'POST',
    body: formData
  })
    .then(function (res) {
      if (!res.ok) throw new Error('main-item-add-failed');
      return res.json();
    })
    .then(function () {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: ENGRAVING_FEE_VARIANT_ID,
            quantity: 1,
            properties: { '_For order': 'Laser engraving fee' }
          }]
        })
      });
    })
    .then(function (res) {
      if (!res || !res.ok) throw new Error('fee-add-failed');
      window.location.href = (window.routes && window.routes.cart_url) || '/cart';
    })
    .catch(function () {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.classList.remove('is-loading');
      }
      self.showError('Something went wrong adding this to your cart. Please try again.');
    });
};

PersonalizationWidget.prototype.validate = function () {
  var hasText = this.textArea && this.textArea.value.trim().length > 0;
  var hasLogo = this.logoInput && this.logoInput.files && this.logoInput.files.length > 0;

  if (!hasText && !hasLogo) {
    return null;
  }

  this.updateOverflowState();
  if (this.hasOverflow) {
    return 'The design extends outside the engraving area — resize or move it to fit before adding to cart.';
  }

  if (this.confirmCheckbox && !this.confirmCheckbox.checked) {
    return "Please confirm you've checked the spelling before continuing — personalized products cannot be returned.";
  }

  return null;
};

PersonalizationWidget.prototype.showError = function (message) {
  if (!this.errorEl) return;
  this.errorEl.textContent = message;
  this.errorEl.hidden = false;
  this.errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

/**
 * Rotates a screen-space delta vector by -angleDeg, i.e. expresses it
 * in the layer's own local (un-rotated) coordinate frame. Used so the
 * resize handle keeps behaving the same relative to the layer itself
 * regardless of how much that layer has been rotated.
 */
function rotateDelta(dx, dy, angleDeg) {
  var rad = -angleDeg * Math.PI / 180;
  return {
    x: dx * Math.cos(rad) - dy * Math.sin(rad),
    y: dx * Math.sin(rad) + dy * Math.cos(rad)
  };
}

/**
 * Converts the image to a true monochrome (duotone). The tint color
 * always applies to the ink itself. Real laser engraving is
 * essentially binary — a point is either engraved (a fully visible
 * mark) or it isn't — so instead of fading opacity smoothly across
 * the whole tonal range (which made mid-tone/gray source ink look
 * like a faint watermark), anything reasonably dark stays FULLY
 * opaque tint, and only the lighter tail (anti-aliased edges, a
 * white background) fades out, over a narrow band for a clean edge.
 */
function duotoneImage(srcDataUrl, tintHex, callback) {
  var img = new Image();
  img.onload = function () {
    var maxDim = 500;
    var scale = Math.min(1, maxDim / Math.max(img.width, img.height));
    var w = Math.max(1, Math.round(img.width * scale));
    var h = Math.max(1, Math.round(img.height * scale));

    var canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    var ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, w, h);

    var imageData;
    try {
      imageData = ctx.getImageData(0, 0, w, h);
    } catch (err) {
      callback(srcDataUrl);
      return;
    }

    var data = imageData.data;
    var tint = hexToRgb(tintHex);

    var lightStart = 0.55;
    var lightEnd = 0.92;
    var band = lightEnd - lightStart;

    for (var i = 0; i < data.length; i += 4) {
      var luminance = (0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]) / 255;
      var originalAlpha = data[i + 3];

      var opacityFactor;
      if (luminance <= lightStart) {
        opacityFactor = 1;
      } else if (luminance >= lightEnd) {
        opacityFactor = 0;
      } else {
        opacityFactor = 1 - ((luminance - lightStart) / band);
      }

      data[i] = tint.r;
      data[i + 1] = tint.g;
      data[i + 2] = tint.b;
      data[i + 3] = originalAlpha * opacityFactor;
    }

    ctx.putImageData(imageData, 0, 0);
    callback(canvas.toDataURL('image/png'));
  };
  img.onerror = function () {
    callback(srcDataUrl);
  };
  img.src = srcDataUrl;
}

function hexToRgb(hex) {
  hex = hex.replace('#', '');
  var bigint = parseInt(hex, 16);
  return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
}
